// SignupPage — the auth screen at /signup. Picks the role via ?role=coach
// (defaults to athlete). On success, navigates to the safe `returnTo`
// (default /app/workout). F-3: a logged-in visitor is redirected to
// returnTo; the signup form is never shown to an authenticated user.

import { useState } from "react";
import type { FormEvent, ReactElement } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "../../i18n";
import { useAuth, type Role } from "../../services/auth";
import { AuthError } from "../../services/authErrors";
import { safeReturnTo } from "../routing/returnTo";

export function SignupPage(): ReactElement {
  const { t } = useI18n();
  const { signUp, user } = useAuth();
  const [params] = useSearchParams();
  const roleFromQuery = params.get("role") === "coach" ? "coach" : "athlete";
  const [role, setRole] = useState<Role>(roleFromQuery);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const returnTo = safeReturnTo(params.get("returnTo"));
  const online = typeof navigator === "undefined" ? true : navigator.onLine;

  if (user) {
    return <Navigate to={returnTo} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!online) return;
    setError(null);
    setSubmitting(true);
    try {
      await signUp({ email, password, displayName, role });
      navigate(returnTo, { replace: true });
    } catch (err) {
      if (err instanceof AuthError) {
        setError(t(authKey(err.code)));
      } else {
        setError(t("auth.error.unknown"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="signup-page"
      className="mx-auto flex min-h-screen w-full max-w-[420px] flex-col gap-6 px-6 py-12"
    >
      <header>
        <p className="font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-lime)]">
          {t("brand.name")}
        </p>
        <h1 className="font-display mt-2 text-4xl uppercase leading-[1.1] tracking-[0.02em] text-[var(--color-ink-hi)]">
          {role === "coach" ? t("auth.signup.titleCoach") : t("auth.signup.title")}
        </h1>
      </header>
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
        data-testid="signup-form"
        aria-disabled={!online}
      >
        <label className="flex flex-col gap-1">
          <span className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
            {t("auth.field.displayName")}
          </span>
          <input
            type="text"
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
            disabled={!online || submitting}
            data-testid="displayName-input"
            className="border border-[var(--color-line-strong)] bg-[var(--color-slab)] px-3 text-[var(--color-ink-hi)]"
            style={{
              minHeight: "64px",
              borderRadius: "var(--radius-sharp)",
              fontSize: 18,
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
            {t("auth.field.email")}
          </span>
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            disabled={!online || submitting}
            data-testid="email-input"
            className="border border-[var(--color-line-strong)] bg-[var(--color-slab)] px-3 text-[var(--color-ink-hi)]"
            style={{
              minHeight: "64px",
              borderRadius: "var(--radius-sharp)",
              fontSize: 18,
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
            {t("auth.field.password")}
          </span>
          <input
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            disabled={!online || submitting}
            data-testid="password-input"
            className="border border-[var(--color-line-strong)] bg-[var(--color-slab)] px-3 text-[var(--color-ink-hi)]"
            style={{
              minHeight: "64px",
              borderRadius: "var(--radius-sharp)",
              fontSize: 18,
            }}
          />
        </label>
        <fieldset className="flex flex-col gap-2">
          <legend className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
            {t("auth.field.roleLabel")}
          </legend>
          <div className="flex gap-2">
            {(["athlete", "coach"] as const).map((r) => {
              const selected = role === r;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  aria-pressed={selected}
                  data-testid={`role-${r}`}
                  className="px-3 py-2 font-subhead text-sm uppercase tracking-[0.06em]"
                  style={{
                    minHeight: "44px",
                    borderRadius: "var(--radius-sharp)",
                    backgroundColor: selected ? "var(--color-lime)" : "transparent",
                    color: selected ? "var(--color-void)" : "var(--color-ink)",
                    border: `1px solid ${selected ? "var(--color-lime)" : "var(--color-line-strong)"}`,
                  }}
                >
                  {t(r === "coach" ? "auth.field.role.coach" : "auth.field.role.athlete")}
                </button>
              );
            })}
          </div>
        </fieldset>
        {error && (
          <p
            data-testid="signup-error"
            role="alert"
            className="font-sans text-sm"
            style={{ color: "var(--color-blood)" }}
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={!online || submitting}
          data-testid="signup-submit"
          className="font-display uppercase tracking-[0.02em] text-[var(--color-void)]"
          style={{
            minHeight: "64px",
            fontSize: 22,
            backgroundColor: "var(--color-lime)",
            borderRadius: "var(--radius-sharp)",
            opacity: !online || submitting ? 0.6 : 1,
          }}
        >
          {t("auth.signup.submit")}
        </button>
      </form>
      <p className="font-sans text-sm text-[var(--color-ink-mute)]">
        {t("auth.signup.haveAccount")}{" "}
        <Link to="/login" className="text-[var(--color-lime)] underline-offset-2 hover:underline">
          {t("auth.signup.loginLink")}
        </Link>
      </p>
    </main>
  );
}

function authKey(code: string): string {
  switch (code) {
    case "wrong-password":
      return "auth.error.wrongPassword";
    case "email-taken":
      return "auth.error.emailTaken";
    case "invalid-email":
      return "auth.error.invalidEmail";
    case "weak-password":
      return "auth.error.weakPassword";
    case "offline":
      return "auth.error.offline";
    default:
      return "auth.error.unknown";
  }
}
